import React, { useRef, useEffect } from 'react'

const OtpInput = ({
  value = '',
  onChange,
  length = 6,
  disabled = false,
  autoFocus = true
}) => {
  const inputRefs = useRef([])

  // Ensure inputRefs length matches length
  useEffect(() => {
    inputRefs.current = inputRefs.current.slice(0, length)
  }, [length])

  // Initial auto focus first empty slot
  useEffect(() => {
    if (autoFocus && inputRefs.current[0]) {
      inputRefs.current[0].focus()
    }
  }, [autoFocus])

  // Array of digits
  const digits = Array.from({ length }, (_, i) => value[i] || '')

  const handleChange = (e, index) => {
    const rawVal = e.target.value.replace(/[^0-9]/g, '')
    if (!rawVal) return

    // If pasted multiple digits into a single box
    if (rawVal.length > 1) {
      handlePasteData(rawVal, index)
      return
    }

    const newChar = rawVal.slice(-1)
    const newDigits = [...digits]
    newDigits[index] = newChar
    const updatedValue = newDigits.join('').slice(0, length)
    onChange(updatedValue)

    // Move focus to next input
    if (index < length - 1 && inputRefs.current[index + 1]) {
      inputRefs.current[index + 1].focus()
    }
  }

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      if (!digits[index] && index > 0 && inputRefs.current[index - 1]) {
        // Current empty, delete previous and move back
        const newDigits = [...digits]
        newDigits[index - 1] = ''
        onChange(newDigits.join(''))
        inputRefs.current[index - 1].focus()
      } else {
        // Delete current digit
        const newDigits = [...digits]
        newDigits[index] = ''
        onChange(newDigits.join(''))
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1].focus()
    } else if (e.key === 'ArrowRight' && index < length - 1) {
      inputRefs.current[index + 1].focus()
    }
  }

  const handlePasteData = (pastedData, startIndex = 0) => {
    const cleanNumbers = pastedData.replace(/[^0-9]/g, '').slice(0, length)
    if (!cleanNumbers) return

    const newDigits = [...digits]
    for (let i = 0; i < cleanNumbers.length; i++) {
      if (startIndex + i < length) {
        newDigits[startIndex + i] = cleanNumbers[i]
      }
    }
    const updatedValue = newDigits.join('').slice(0, length)
    onChange(updatedValue)

    // Focus on the last filled or next slot
    const nextIndex = Math.min(startIndex + cleanNumbers.length, length - 1)
    if (inputRefs.current[nextIndex]) {
      inputRefs.current[nextIndex].focus()
    }
  }

  const handlePaste = (e, index) => {
    e.preventDefault()
    const pastedData = e.clipboardData.getData('text')
    handlePasteData(pastedData, index)
  }

  return (
    <div className="flex items-center justify-center gap-2 sm:gap-3 my-3">
      {Array.from({ length }).map((_, index) => {
        const isFilled = Boolean(digits[index])
        return (
          <input
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            inputMode="numeric"
            autoComplete={index === 0 ? 'one-time-code' : 'off'}
            maxLength={1}
            disabled={disabled}
            value={digits[index]}
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            onPaste={(e) => handlePaste(e, index)}
            onFocus={(e) => e.target.select()}
            className={`w-11 h-13 sm:w-12 sm:h-14 text-center text-xl sm:text-2xl font-extrabold rounded-2xl border-2 transition-all duration-200 outline-hidden select-all ${
              disabled
                ? 'bg-gray-100 border-gray-200 text-gray-400 cursor-not-allowed'
                : isFilled
                ? 'border-primary-600 bg-purple-50/50 text-primary-950 shadow-xs ring-2 ring-primary-100'
                : 'border-gray-200 bg-white text-gray-800 hover:border-purple-300 focus:border-primary-600 focus:ring-4 focus:ring-primary-100/60 focus:bg-white'
            }`}
          />
        )
      })}
    </div>
  )
}

export default OtpInput
